// src/components/SiteConfig/components/Coordinates/Coordinates.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useLoadScript,
  GoogleMapProps,
} from '@react-google-maps/api';
import { FaSave, FaMapMarker } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Button } from 'react-bootstrap';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import { updateCoordinates } from '../../../../../../../../crud/management_api/local_site_configuration';
import { LocalSiteConfig, Coordinates } from '../../types';
import './Coordinates.scss';

const LIBRARIES = ['places'] as const;
const WORLD_CENTER = { lat: 0, lng: 0 };
const WORLD_ZOOM = 2;
const FOCUSED_ZOOM = 15;
const MAP_STYLE = { width: '100%', height: '300px' };

interface Props {
  config: LocalSiteConfig;
  setConfig: React.Dispatch<React.SetStateAction<LocalSiteConfig>>;
}

const CoordinatesManager: React.FC<Props> = ({ config, setConfig }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyC_64r5t1Wsogt7zdpfvuLnjCUGcSju_jU',
    libraries: [...LIBRARIES],
  });

  // initialize coords from backend if present
  const saved = config.site_coordinates;
  const initial = useMemo<Coordinates | null>(() => {
    if (saved && Array.isArray(saved) && saved.length === 2) {
      return { latitude: saved[0], longitude: saved[1] };
    }
    return null;
  }, [saved]);

  const [coords, setCoords] = useState<Coordinates | null>(initial);
  const [center, setCenter] = useState(
    initial ? { lat: initial.latitude, lng: initial.longitude } : WORLD_CENTER
  );
  const [zoom, setZoom] = useState(initial ? FOCUSED_ZOOM : WORLD_ZOOM);

  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [placeId, setPlaceId] = useState<string | null>(config.place_id ?? null);
  const [placeName, setPlaceName] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // fetch a place's human name for initial display
  const fetchPlaceName = async (pid: string): Promise<string> => {
    if (!pid || !isLoaded) return '';
    const service = new google.maps.places.PlacesService(
      document.createElement('div')
    );
    return new Promise((resolve) => {
      service.getDetails({ placeId: pid }, (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.formatted_address
        ) {
          resolve(place.formatted_address);
        } else {
          resolve('');
        }
      });
    });
  };

  // keep placeId in sync if server pushes a new one
  useEffect(() => {
    setPlaceId(config.place_id ?? null);
  }, [config.place_id]);

  // when placeId changes (or load finishes), fetch its name
  useEffect(() => {
    if (isLoaded && placeId) {
      fetchPlaceName(placeId).then((name) => setPlaceName(name));
    } else {
      setPlaceName('');
    }
  }, [placeId, isLoaded]);

  // on-load: either use saved coords or geolocate
  useEffect(() => {
    if (!isLoaded) return;
    if (initial) {
      setCenter({ lat: initial.latitude, lng: initial.longitude });
      setZoom(FOCUSED_ZOOM);
    } else if (!saved && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setCoords({ latitude, longitude });
          setCenter({ lat: latitude, lng: longitude });
          setZoom(FOCUSED_ZOOM);
        },
        () => toast.warning('No pudimos obtener tu ubicación.')
      );
    }
  }, [isLoaded, initial, saved]);

  // Autocomplete handlers
  const onLoadAuto = (ac: google.maps.places.Autocomplete) => {
    setAutocomplete(ac);
  };
  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    const pid = place.place_id ?? null;
    setPlaceId(pid);

    // update the input field with the “official” address
    const address = place.formatted_address || place.name || '';
    setPlaceName(address);

    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setCoords({ latitude: lat, longitude: lng });
      setCenter({ lat, lng });
      setZoom(FOCUSED_ZOOM);
    }
  };

  // clicking on map
  const onMapClick: GoogleMapProps['onClick'] = useCallback((e: any) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat(),
      lng = e.latLng.lng();
    setCoords({ latitude: lat, longitude: lng });
    setCenter({ lat, lng });
    setZoom(FOCUSED_ZOOM);
    // clearing placeId since user manually moved marker
    setPlaceId(null);
    setPlaceName(''); // clear the input to show manual marker
  }, []);

  // save both coords + placeId
  const saveCoords = async () => {
    if (!coords) return toast.warning('Selecciona ubicación primero.');
    setSaving(true);
    try {
      const payload = {
        latitude: coords.latitude,
        longitude: coords.longitude,
      };
      const user = localStorage.getItem('user');
      const localId = user ? JSON.parse(user).local_id : null;
      if (!localId) {
        toast.error('No se pudo obtener el local_id');
        return;
      }
      await updateCoordinates(localId, payload, placeId);
      setConfig((r) => ({
        ...r,
        site_coordinates: { latitude: coords.latitude, longitude: coords.longitude },
        place_id: placeId,
      }));
      toast.success('Coordenadas guardadas');
    } catch {
      toast.error('Error al guardar coordenadas');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div>Error cargando Google Maps</div>;
  if (!isLoaded) return <MiniLoadingSpinner />;

  return (
    <div className="coordinates-manager-container">
      <h4>Selecciona la ubicación</h4>

      <Autocomplete onLoad={onLoadAuto} onPlaceChanged={onPlaceChanged}>
        <input
          type="text"
          className="search-input"
          placeholder="Buscar dirección…"
          value={placeName}
          onChange={(e) => {
            setPlaceName(e.target.value);
            setPlaceId(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        />
      </Autocomplete>

      <GoogleMap
        mapContainerStyle={MAP_STYLE}
        center={center}
        zoom={zoom}
        onClick={onMapClick}
      >
        {coords && (
          <Marker
            position={{ lat: coords.latitude, lng: coords.longitude }}
            draggable
            onDragEnd={(e) => {
              const lat = e.latLng?.lat(),
                lng = e.latLng?.lng();
              if (lat != null && lng != null) {
                setCoords({ latitude: lat, longitude: lng });
                setCenter({ lat, lng });
                setPlaceId(null);
                setPlaceName('');
              }
            }}
          />
        )}
      </GoogleMap>

      <div className="coord-controls">
        <Button
          variant="none"
          onClick={() => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const { latitude, longitude } = pos.coords;
                setCoords({ latitude, longitude });
                setCenter({ lat: latitude, lng: longitude });
                setZoom(FOCUSED_ZOOM);
                setPlaceId(null);
                setPlaceName('');
              },
              () => toast.error('No se pudo obtener ubicación')
            );
          }}
        >
          <FaMapMarker /> Usar mi ubicación
        </Button>{' '}
        <Button variant="primary" disabled={saving} onClick={saveCoords}>
          <FaSave /> {saving ? <MiniLoadingSpinner /> : 'Guardar ubicación'}
        </Button>
      </div>
    </div>
  );
};

export default CoordinatesManager;
